import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Label } from '../atoms/label.jsx';
import StatusDot from '../atoms/statusdot.jsx';
import Row from '../organism/row.jsx';
import LoadingSpinner from '../organism/spinner.jsx';
import { COLOR, SIZE, FONT_FAMILY, SCREEN_MEDIA, URL } from '../../utils/constants.js';
import { useDecorationTypes } from '../hooks/useDecorationTypes.js';
import { useBookingList } from '../hooks/useBookingList.js';

const EMPTY_VALUE = '-';

const Container = styled.div`
    overflow: auto; // To cover height with contents
    padding: 0px 15px;
    @media only screen and ${SCREEN_MEDIA.small} {
        margin: 0px 2px;
    }
    @media only screen and ${SCREEN_MEDIA.medium} {
        margin: 30px 80px;
    }
    @media only screen and ${SCREEN_MEDIA.long} {
        margin: 30px 150px;
    }
    @media only screen and ${SCREEN_MEDIA.xlong} {
        margin: 30px 250px;
    }
`;

const MessageCard = styled.div`
    padding: 25px;
    background-color: ${COLOR.background};
    border-radius: 25px;
    box-shadow: -1px 0px 22px -3px rgba(153,153,153,1);
`;

const List = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin: 0;
    padding: 0;
    list-style: none;
`;

// A real <button> so each booking can be opened with the keyboard too.
const BookingCard = styled.button`
    display: block;
    width: 100%;
    padding: 20px 25px;
    text-align: left;
    background-color: ${COLOR.background};
    border: 2px solid transparent;
    border-radius: 25px;
    box-shadow: -1px 0px 22px -3px rgba(153,153,153,1);
    cursor: pointer;
    opacity: ${props => props.is_past ? 0.6 : 1};
    transition: border-color 0.2s, transform 0.2s;
    &:hover, &:focus-visible {
        border-color: ${COLOR.ferra};
        transform: translateY(-2px);
        outline: none;
    }
    p {
        overflow-wrap: anywhere; // Long addresses / names must not overflow the card
    }
`;

// Status circle + booking id.
const CardTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 15px;
    padding: 0 0 10px 0;
    border-bottom: 2px solid ${COLOR.grayClear};
`;

// Label / value pairs: two columns on wider screens, stacked on phones.
const InfoRow = styled.div`
    display: grid;
    grid-template-columns: 140px 1fr;
    column-gap: 20px;
    align-items: baseline;
    @media only screen and ${SCREEN_MEDIA.small} {
        grid-template-columns: 1fr;
    }
`;

const hasValue = (value) => value !== null && value !== undefined && value !== '';

// Event date in the user's local time, same as the detail screen.
const formatEventDateTime = (value) => {
    const date = new Date(value ?? '');
    if (!value || isNaN(date)) return EMPTY_VALUE;
    return date.toLocaleString('es-PE', {
        weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
    });
};

// The list only carries the type ids: titles come from the event types list.
const getTypeTitle = (types, typeCatLv1Id, typeCatLv2Id) => {
    const typeCatLv1 = (types?.categoriesLv1 ?? []).find(item => String(item.id) === String(typeCatLv1Id));
    const typeCatLv2 = (typeCatLv1?.categoriesLv2 ?? []).find(item => String(item.id) === String(typeCatLv2Id));
    return [typeCatLv1?.title, typeCatLv2?.title].filter(Boolean).join(' · ') || EMPTY_VALUE;
};

const Info = ({ title, value }) => (
    <InfoRow>
        <Label
            margin='6px 0'
            text_size={SIZE.body}
            text_color={COLOR.grayDark}
            font_family={FONT_FAMILY.avenirMedium}>
            { title }
        </Label>
        <Label
            margin='6px 0'
            text_size={SIZE.body}
            text_color={COLOR.black}
            font_family={FONT_FAMILY.avenirMedium}>
            { hasValue(value) ? value : EMPTY_VALUE }
        </Label>
    </InfoRow>
);

const Message = ({ children }) => (
    <MessageCard>
        <Label
            text_size={SIZE.subtitle}
            text_color={COLOR.ferra}
            font_family={FONT_FAMILY.avenirMedium}>
            { children }
        </Label>
    </MessageCard>
);

const BodyBookingList = () => {

    const navigate = useNavigate();
    const { data: decorationTypes, isLoading: isTypesLoading, error: typesError } = useDecorationTypes();
    const { data: bookingList, isLoading: isBookingListLoading, error } = useBookingList();

    const openBooking = (bookingId) => {
        navigate(URL.bookingDetail.replace(':bookingId', encodeURIComponent(bookingId)));
    };

    // Already sorted by the repository: filtering keeps that order in each group.
    const { upcomingBookings, pastBookings } = useMemo(() => ({
        upcomingBookings: (bookingList ?? []).filter(booking => !booking.isPast),
        pastBookings: (bookingList ?? []).filter(booking => booking.isPast)
    }), [bookingList]);

    const renderTitle = (title, count, margin) => (
        <Label
            margin={margin}
            text_size={SIZE.title}
            text_color={COLOR.ferra}
            font_family={FONT_FAMILY.avenirMedium}>
            { count > 0 ? `${title} (${count})` : title }
        </Label>
    );

    const renderBookings = (bookings) => (
        <List>
            { bookings.map((booking) => {
                return (
                    <li key={ booking.id }>
                        <BookingCard
                            type='button'
                            is_past={ booking.isPast ? 1 : 0 }
                            onClick={ () => openBooking(booking.id) }>
                            <CardHeader>
                                <CardTitle>
                                    <StatusDot booking={ booking }/>
                                    <Label
                                        margin='0'
                                        text_size={SIZE.title}
                                        text_color={COLOR.ferra}
                                        font_family={FONT_FAMILY.avenirMedium}>
                                        { `Reserva #${booking.id}` }
                                    </Label>
                                </CardTitle>
                                { booking.isPast &&
                                    <Label
                                        margin='0'
                                        text_size={SIZE.body}
                                        text_color={COLOR.grayDark}
                                        font_family={FONT_FAMILY.avenirMedium}>
                                        Evento realizado
                                    </Label> }
                            </CardHeader>
                            <Info title='Evento' value={ getTypeTitle(decorationTypes, booking.typeCatLv1Id, booking.typeCatLv2Id) }/>
                            <Info title='Fecha' value={ formatEventDateTime(booking.eventDateTime) }/>
                            <Info title='Organizador' value={ booking.organizerName }/>
                        </BookingCard>
                    </li>
                );
            }) }
        </List>
    );

    const renderContent = () => {
        if (isBookingListLoading || isTypesLoading || !bookingList || !decorationTypes) return <LoadingSpinner/>;

        if (error || typesError) {
            return <Message>Ocurrió un error al cargar las reservas. Inténtalo nuevamente.</Message>;
        }

        return (
            <>
                { upcomingBookings.length > 0
                    ? renderBookings(upcomingBookings)
                    : <Message>Aún no hay reservas próximas.</Message> }

                { pastBookings.length > 0 &&
                    <>
                        { renderTitle('Reservas pasadas', pastBookings.length, '50px 0 25px 0') }
                        { renderBookings(pastBookings) }
                    </> }
            </>
        );
    };

    return (
        <div>
            <Container>
                <Row margin='40px 0px'>
                    { renderTitle('Reservas', upcomingBookings.length, '0 0 25px 0') }
                    { renderContent() }
                </Row>
            </Container>
        </div>
    );
}

export default BodyBookingList;
